/**
 * ForceUpdateScreen — the blocking, non-dismissible "you must update" wall.
 *
 * Shown by ForceUpdateGate when the running build is below the enforced floor
 * for its store. The only ways out are Update (opens the store) or, on Android,
 * Quit. The hardware back button is swallowed so the screen can't be dismissed
 * or the app backgrounded out from under it (iOS has no programmatic quit).
 */
import { useEffect } from "react";
import { DownloadCloud } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Button } from "@/components/ui/button";
import { openStore, type UpdateStatus } from "@/lib/app-version";

export function ForceUpdateScreen({ status }: { status: UpdateStatus | null }) {
  const canQuit = Capacitor.getPlatform() === "android";

  // Trap the hardware back button while the force screen is up.
  useEffect(() => {
    const sub = CapacitorApp.addListener("backButton", () => { /* swallow — no exit */ });
    return () => { void sub.then((h) => h.remove()); };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-6 bg-bg px-6 text-center text-fg"
      style={{
        paddingTop: "max(1rem, env(safe-area-inset-top))",
        paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
      }}
      role="alertdialog"
      aria-modal="true"
      aria-label="Update required"
    >
      <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
        <DownloadCloud className="size-8 text-primary" />
      </div>
      <div className="max-w-sm space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">Update required</h1>
        <p className="text-sm leading-relaxed text-fg-muted">
          {status?.message ??
            "A newer version of iCheck is required to keep working. Please update to the latest version to continue."}
          {status?.latestVersion ? ` (v${status.latestVersion})` : ""}
        </p>
      </div>
      <div className="flex w-full max-w-xs flex-col gap-2">
        <Button size="lg" onClick={() => openStore(status?.storeUrl ?? null)}>
          <DownloadCloud className="mr-1 size-4" /> Update now
        </Button>
        {canQuit ? (
          <Button size="lg" variant="outline" onClick={() => void CapacitorApp.exitApp()}>
            Quit
          </Button>
        ) : null}
      </div>
    </div>
  );
}
